import { useCallback, useEffect, useRef, useState } from 'react'

import { hasToken } from '../../lib/identity'
import {
  LeaderboardEntry,
  fetchLeaderboard,
  formatTime,
  getPlayerName,
  ordinal,
  setPlayerName,
  submitScore,
} from '../../lib/leaderboard'
import { getStoredIsHighContrastMode } from '../../lib/localStorage'

type Props = {
  // Puzzle identifier (solutionIndex) — the day's puzzle number.
  day: number
  isGameWon: boolean
  isGameLost: boolean
}

type Phase = 'name' | 'submitting' | 'loading' | 'ready' | 'error'

// Last table we showed. The window unmounts whenever it closes, so without
// this every reopen (e.g. from the Save the Date card) would flash
// "Loading..." and then jump to the full table. Reopens show this instantly
// and refresh quietly.
let lastEntries: LeaderboardEntry[] | null = null

// Warm the cache ahead of time (e.g. while the Save the Date card is up), so
// the first open of the window already has its full table.
export const prefetchLeaderboard = (): Promise<void> =>
  fetchLeaderboard()
    .then((fresh) => {
      lastEntries = fresh
    })
    .catch(() => {})

export const Leaderboard = ({ day, isGameWon, isGameLost }: Props) => {
  const gameOver = isGameWon || isGameLost
  const hc = getStoredIsHighContrastMode()
  // Scroll your own row into view once, not on every background refresh.
  const scrolledToMe = useRef(false)
  const [name, setName] = useState(getPlayerName())
  const [draftName, setDraftName] = useState(getPlayerName())
  const [phase, setPhase] = useState<Phase>(
    !getPlayerName() ? 'name' : lastEntries ? 'ready' : 'loading'
  )
  const [entries, setEntries] = useState<LeaderboardEntry[]>(
    () => lastEntries || []
  )
  const [error, setError] = useState('')

  const load = useCallback(async (silent = false) => {
    if (!silent) setPhase('loading')
    try {
      const fresh = await fetchLeaderboard()
      lastEntries = fresh
      setEntries(fresh)
      setPhase('ready')
    } catch (e: any) {
      // On a silent background refresh, keep the current table rather than
      // replacing it with an error screen.
      if (!silent) {
        setError(e?.message || 'Could not load the leaderboard.')
        setPhase('error')
      }
    }
  }, [])

  // Load the board. For a SIGNED-IN guest the result was already persisted and
  // submitted in App.tsx before this modal opened, so here we only load (no
  // duplicate submit, no misleading "Posting your result…"). For anonymous/dev
  // play (no token) there is no upstream submit, so submit here as before.
  const submitAndLoad = useCallback(
    async (playerName: string) => {
      // Signed-in, or game not over: nothing to submit from here — just load.
      if (!gameOver || hasToken()) {
        // With a cached table on screen, refresh quietly (no "Loading...").
        await load(!!lastEntries)
        return
      }
      setPhase('submitting')
      try {
        await submitScore({ day })
      } catch (e: any) {
        // Non-fatal: still show the board even if the submit failed.
        setError(e?.message || 'Could not submit your score.')
      }
      await load()
    },
    [day, gameOver, load]
  )

  useEffect(() => {
    if (name) {
      submitAndLoad(name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-refresh the board every 10s while it is shown (no manual button).
  // The GET is edge-cached 30s, so polling faster than that just hits cache.
  useEffect(() => {
    if (!name) return
    const id = setInterval(() => {
      load(true)
    }, 10000)
    return () => clearInterval(id)
  }, [name, load])

  const handleSaveName = () => {
    const clean = draftName.replace(/\s+/g, ' ').trim().slice(0, 20)
    if (clean.length < 1) return
    setPlayerName(clean)
    setName(clean)
    submitAndLoad(clean)
  }

  if (phase === 'name') {
    return (
      <div className="mt-4 border-t border-gray-200 pt-4 dark:border-slate-700">
        <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Join the leaderboard
        </h4>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          Pick a name to post your result and see how you rank.
        </p>
        <div className="mt-2 flex items-center justify-center gap-2">
          <input
            type="text"
            maxLength={20}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
            placeholder="Your name"
            className="w-40 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
          />
          <button
            onClick={handleSaveName}
            className="rounded bg-green-700 px-3 py-1 text-sm font-semibold text-white hover:bg-green-800"
          >
            Save
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="dark:border-gray-600">
      <h4 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
        Leaderboard
      </h4>

      {(phase === 'loading' || phase === 'submitting') && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
          {phase === 'submitting' ? 'Posting your result…' : 'Loading…'}
        </p>
      )}

      {phase === 'error' && (
        <p className="mt-2 text-sm text-red-500">
          {error}{' '}
          <button className="underline" onClick={load}>
            Retry
          </button>
        </p>
      )}

      {phase === 'ready' && (
        <>
          {error && (
            <p className="mt-1 text-xs text-amber-500">
              {error} (your score may not be shown)
            </p>
          )}
          {entries.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
              No scores yet. Be the first!
            </p>
          ) : (
            <>
              {(() => {
                const myIndex = entries.findIndex((e) => e.player === name)
                if (myIndex === -1) return null
                // A loss isn't a placing worth celebrating in green.
                if (entries[myIndex].won === 0) {
                  return (
                    <p className="mt-1 text-sm font-semibold text-gray-600 dark:text-gray-300">
                      Out of guesses this time. You&rsquo;re on the board.
                    </p>
                  )
                }
                return (
                  <p
                    className={`mt-1 text-sm font-semibold ${
                      hc
                        ? 'text-orange-700 dark:text-orange-400'
                        : 'text-green-700 dark:text-green-400'
                    }`}
                  >
                    You finished in {ordinal(myIndex + 1)}
                    {entries.length >= 100 ? ' (of top 100)' : ''}
                  </p>
                )
              })()}
              <div className="mt-3 max-h-[288px] overflow-y-auto overflow-x-hidden dark:[color-scheme:dark]">
                <table className="w-full table-fixed border-collapse text-sm text-gray-900 dark:text-gray-100">
                  <thead className="sticky top-0 bg-white dark:bg-slate-800">
                    <tr className="h-8 border-b border-gray-200 text-xs text-gray-500 dark:border-slate-700 dark:text-gray-400">
                      <th className="w-9 py-2 pl-1 pr-2 text-left font-semibold">
                        <span className="inline-flex w-6 justify-center">
                          #
                        </span>
                        <span className="sr-only">Rank</span>
                      </th>
                      <th className="py-2 pr-2 text-left font-semibold">
                        Player
                      </th>
                      <th className="w-[3.75rem] py-2 pr-2 text-right font-semibold">
                        Guesses
                      </th>
                      <th className="w-[3.75rem] py-2 pr-3 text-right font-semibold">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const isMe = e.player === name
                      const failed = e.won === 0
                      // Podium ranks are drawn as mini game tiles in the
                      // board's own colors: correct, present, absent.
                      const podium = failed
                        ? null
                        : i === 0
                        ? hc
                          ? 'bg-orange-500'
                          : 'bg-green-500'
                        : i === 1
                        ? hc
                          ? 'bg-cyan-500'
                          : 'bg-yellow-500'
                        : i === 2
                        ? 'bg-slate-400 dark:bg-slate-600'
                        : null
                      return (
                        <tr
                          key={`${e.player}-${i}`}
                          ref={
                            isMe
                              ? (el) => {
                                  if (el && !scrolledToMe.current) {
                                    scrolledToMe.current = true
                                    el.scrollIntoView({ block: 'nearest' })
                                  }
                                }
                              : undefined
                          }
                          className={`h-10 border-b border-gray-100 last:border-0 dark:border-slate-700/60 ${
                            isMe
                              ? hc
                                ? 'bg-orange-500/15 font-semibold'
                                : 'bg-green-500/15 font-semibold'
                              : ''
                          }`}
                        >
                          <td className="py-2 pl-1 pr-2 text-left tabular-nums">
                            {podium ? (
                              <span
                                className={`inline-flex h-6 w-6 items-center justify-center rounded text-xs font-bold text-white ${podium}`}
                              >
                                {i + 1}
                              </span>
                            ) : (
                              <span className="inline-flex w-6 justify-center text-gray-500 dark:text-gray-400">
                                {i + 1}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-2 text-left">
                            <div className="flex min-w-0 items-center">
                              <span className="truncate" title={e.player}>
                                {e.player}
                              </span>
                              {isMe && (
                                <span
                                  className={`ml-1.5 hidden shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white sm:inline ${
                                    hc ? 'bg-orange-700' : 'bg-green-700'
                                  }`}
                                >
                                  You
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums">
                            <span
                              className={
                                failed ? 'text-gray-500 dark:text-gray-400' : ''
                              }
                            >
                              {failed ? 'X/6' : `${e.guesses}/6`}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatTime(e.timeMs)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
